import { useEffect, useRef } from 'react'
import type { MessagesWithAds, UserData } from '..'
import { SendBtnIcon } from '@/assets/icons'

interface MessageFormProps {
  input: string
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  handleSubmit: () => void
  isTyping: boolean
  userData: Partial<UserData>
  messages: MessagesWithAds[]
  currentRequestId?: string | null
  onStopRequest?: (requestId: string) => void
}

const MessageForm = ({
  input,
  handleChange,
  handleKeyDown,
  handleSubmit,
  isTyping,
  userData,
  messages,
  currentRequestId,
  onStopRequest,
}: MessageFormProps) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    if (!isTyping) {
      inputRef.current?.focus()
    }
  }, [messages.length, isTyping])
  return (
    <div className='mt-auto bg-[#f6f6f6] shadow-[-4px_0_16px_0_#00000014] border-none'>
      <div className='mb-2 text-center'>
        {messages.length === 0 && (
          <h1 className='text-xl font-semibold text-gray-700'>
            Hello {userData?.name ?? ''}, how can I assist you today?
          </h1>
        )}
      </div>

      <form
        onSubmit={e => e.preventDefault()}
        className='flex flex-col gap-3 pt-0 px-[15px] pb-[10px]'
      >
        <div className='flex gap-3 relative'>
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder='Type your question here...'
            rows={3}
            // maxLength={500}
            disabled={isTyping}
            className={`w-full bg-[#dadcdf] h-16 border-none font-normal text-[14px] sm:text-[16px] leading-[100%] outline-none text-[#444444] px-5 py-[10px] rounded-[10px] resize-none ${
              isTyping && currentRequestId && onStopRequest
                ? 'pr-[100px]'
                : 'pr-[40px]'
            }`}
          />
          {isTyping && currentRequestId && onStopRequest ? (
            <button
              type='button'
              onClick={() => onStopRequest(currentRequestId)}
              className='bg-[#ef4444] hover:bg-[#dc2626] text-white border-none px-4 py-2 rounded-[6px] cursor-pointer inline-flex items-center gap-2 font-medium absolute right-[15px] left-auto top-1/2 -translate-y-1/2 transition-colors'
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='currentColor'
                className='flex-shrink-0'
              >
                <rect x='2' y='2' width='12' height='12' rx='1' />
              </svg>
              <span className='text-sm'>Stop</span>
            </button>
          ) : (
            <button
              type='button'
              onClick={handleSubmit}
              disabled={input.trim() === '' || isTyping}
              className='cursor-pointer bg-transparent absolute right-[15px] left-auto top-1/2 -translate-y-1/2 transition disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <SendBtnIcon />
            </button>
          )}
        </div>

        {/* Character Count Display */}
        <div className='text-right text-xs text-gray-600 pr-1'>
          {input.length}/1000
        </div>
      </form>
    </div>
  )
}

export default MessageForm
